import { handleResponse } from "utils/utils";
import { authService } from "services";
import { isDocumentFolderDebugEnabled, logAttachmentFolderFields } from "utils/documentFolderDebug";

export const documentsService = {
    // store-folders
    getStoreFolders,
    getStoreFolderById,
    createStoreFolder,
    updateStoreFolder,
    deleteStoreFolder,

    // attachments
    getAttachments,
    uploadAttachments,
    deleteAttachment,
    moveAttachment,
    renameAttachment,
    uploadMassiveAttachments,
    uploadMassiveSingleFile,
    signAttachment,
    getSignaturitAttachments,
    getRecentAttachments,
    getPendingSignatures,
};

// =================================================================
// Store Folders
// =================================================================

/**
 * 5. GET /api/store-folders
 * Propósito: Buscar/listar store folders
 * Filtros: Parámetro q para búsqueda por nombre (like search)
 */
function getStoreFolders(query) {
    if (query === undefined)
        query = '';

    const requestOptions = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(`${global.BASE_API}store-folders?q=${query}`, requestOptions).then(handleResponse);
}

/**
 * 1. GET /api/store-folders/{store-folder-id}
 * Propósito: Obtener un store folder específico por su ID
 */
function getStoreFolderById(id) {
    const requestOptions = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(`${global.BASE_API}store-folders/${id}`, requestOptions).then(handleResponse);
}

/**
 * 2. POST /api/store-folders
 * Propósito: Crear un nuevo store folder
 * Body: StoreFolderDTO (name, description, icon)
 */
function createStoreFolder(storeFolderDTO) {
    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(storeFolderDTO)
    };

    return fetch(`${global.BASE_API}store-folders`, requestOptions).then(handleResponse);
}

/**
 * 3. PUT /api/store-folders/{store-folder-id}
 * Propósito: Editar un store folder existente
 * Body: StoreFolderDTO con los datos a actualizar
 */
function updateStoreFolder(id, storeFolderDTO) {
    const requestOptions = {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(storeFolderDTO)
    };

    return fetch(`${global.BASE_API}store-folders/${id}`, requestOptions).then(handleResponse);
}

/**
 * 4. DELETE /api/store-folders/{store-folder-id}
 * Propósito: Eliminar un store folder
 */
function deleteStoreFolder(id) {
    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(`${global.BASE_API}store-folders/${id}`, requestOptions).then(handleResponse);
}


// =================================================================
// Attachments
// =================================================================

/**
 * 8. GET /api/store-folders/{store-folder-id}/attachments
 * Propósito: Obtener todos los attachments de un store folder
 * ⭐ FILTRO POR USUARIO: Parámetro employee_id opcional para filtrar por empleado
 */
function getAttachments(storeFolderId, employeeId) {
    let url = `${global.BASE_API}store-folders/${storeFolderId}/attachments`;
    const params = [];

    if (employeeId) {
        params.push(`employee_id=${encodeURIComponent(employeeId)}`);
    }

    if (params.length > 0) {
        url += `?${params.join('&')}`;
    }

    const requestOptions = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(url, requestOptions).then(response =>
        handleResponse(response, { ignore401: true })
    );
}


/**
 * 6. POST /api/store-folders/{store-folder-id}/attachments
 * Propósito: Subir archivos/attachments a un store folder
 * Content-Type: multipart/form-data
 * Requiere: AttachmentStoreFolderDTO + archivos
 */
function uploadAttachments(storeFolderId, employeeId, files) {
    const formData = new FormData();

    // Crear un Blob con el tipo de contenido correcto y un nombre de archivo
    // Siempre incluir employee_id si se proporciona (puede ser el ID del usuario que sube)
    const attachmentDTO = employeeId ?
        JSON.stringify({ employee_id: employeeId }) :
        JSON.stringify({});
    const blob = new Blob([attachmentDTO], { type: 'application/json' });
    formData.append('attachment', blob, 'attachment.json'); // Añadir un nombre de archivo

    // Adjuntar ficheros. El backend espera un 'part' llamado 'filename'.
    for (const file of files) {
        formData.append('filename', file);
    }

    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
        },
        body: formData,
    };

    return fetch(`${global.BASE_API}store-folders/${storeFolderId}/attachments`, requestOptions).then(handleResponse);
}

/**
 * 7. DELETE /api/store-folders/{store-folder-id}/attachments/{attachment-uuid}
 * Propósito: Eliminar un attachment específico de un store folder
 */
function deleteAttachment(storeFolderId, attachmentUuid) {
    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(`${global.BASE_API}store-folders/${storeFolderId}/attachments/${attachmentUuid}`, requestOptions).then(handleResponse);
}

/**
 * 8. POST /api/store-folders/{store-folder-id}/attachments/{attachment-uuid}/move/{target-store-folder-id}
 * Propósito: Mover un attachment de una carpeta a otra
 */
function moveAttachment(storeFolderId, attachmentUuid, targetStoreFolderId) {
    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
            'Content-Type': 'application/json'
        },
    };

    return fetch(`${global.BASE_API}store-folders/${storeFolderId}/attachments/${attachmentUuid}/move/${targetStoreFolderId}`, requestOptions).then(handleResponse);
}

/**
 * 9. PUT /api/store-folders/{store-folder-id}/attachments/{attachment-uuid}
 * Propósito: Renombrar un attachment
 * Body: { "name": "nuevo-nombre.pdf" }
 */
function renameAttachment(storeFolderId, attachmentUuid, newName) {
    const requestOptions = {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName })
    };

    return fetch(`${global.BASE_API}store-folders/${storeFolderId}/attachments/${attachmentUuid}`, requestOptions).then(handleResponse);
}

/**
 * 9. POST /api/store-folders/{store-folder-id}/attachments/massive
 * Propósito: Subir archivos/attachments de forma masiva a un store folder
 * Content-Type: multipart/form-data
 * Requiere: AttachmentStoreFolderDTO + archivos + employee_identifier
 */
function uploadMassiveAttachments(storeFolderId, files, employeeIdentifier) {
    const formData = new FormData();

    // Crear el JSON con el formato correcto (camelCase)
    const attachmentDTO = {
        employee_identifier: employeeIdentifier
    };

    // Crear un Blob con el JSON para enviarlo como @RequestPart
    const blob = new Blob([JSON.stringify(attachmentDTO)], { type: 'application/json' });
    formData.append('attachment', blob, 'attachment.json');

    // Adjuntar cada archivo individualmente con el mismo nombre de campo
    // El backend espera múltiples archivos con el mismo nombre de campo 'filename'
    for (const file of files) {
        formData.append('filename', file);
    }

    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
        console.log(key, value);
    }

    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
        },
        body: formData,
    };

    return fetch(`${global.BASE_API}store-folders/${storeFolderId}/attachments/massive`, requestOptions).then(handleResponse);
}

/**
 * 10. POST /api/store-folders/{store-folder-id}/attachments/massive/single
 * Propósito: Subir un archivo PDF de múltiples páginas para procesamiento masivo
 * Content-Type: multipart/form-data
 * Requiere: un archivo PDF
 */
function uploadMassiveSingleFile(storeFolderId, file, customName, atomicProcessing = true) {
    const formData = new FormData();

    // Adjuntar el archivo PDF
    formData.append('filename', file);
    
    // Solo adjuntar el nombre personalizado si se proporciona y no está vacío
    if (customName && customName.trim() !== '') {
        formData.append('custom_name', customName);
    }

    // Agregar atomic_processing (por defecto true)
    // FormData convierte booleanos a strings automáticamente
    // Asegurarnos de que se envíe el valor booleano correcto
    // Si atomicProcessing es false, enviamos false (que FormData convertirá a "false")
    // Si atomicProcessing es true, enviamos true (que FormData convertirá a "true")
    console.log('uploadMassiveSingleFile - atomicProcessing received:', atomicProcessing, 'type:', typeof atomicProcessing);
    formData.append('atomic_processing', atomicProcessing);
    
    // Verificar qué se está enviando en FormData
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
        console.log(key, value, typeof value);
    }

    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
        },
        body: formData,
    };

    return fetch(`${global.BASE_API}store-folders/${storeFolderId}/attachments/massive/single`, requestOptions).then(handleResponse);
}

/**
 * 11. POST /api/store-folders/{store-folder-id}/attachments/{attachment-uuid}/sign
 * Propósito: Enviar un documento para firma con Signaturit
 * Retorna: Respuesta procesada que maneja tanto errores HTTP como errores en el contenido de la respuesta
 */
async function signAttachment(storeFolderId, attachmentUuid, signData = null) {
    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`,
            'Content-Type': 'application/json'
        },
    };

    // Si se proporcionan datos de firma, incluirlos en el body
    if (signData) {
        requestOptions.body = JSON.stringify(signData);
    }

    try {
        const response = await fetch(`${global.BASE_API}store-folders/${storeFolderId}/attachments/${attachmentUuid}/sign`, requestOptions);
        
        // Leer el cuerpo de la respuesta
        const responseData = await response.json();

        // Verificar si hay errores en el contenido de la respuesta
        if (responseData.status && responseData.status >= 400) {
            // Hay un error en el contenido de la respuesta
            const errorMessage = responseData.errors && responseData.errors.length > 0 
                ? responseData.errors[0].description || responseData.message || 'Error al procesar la firma'
                : responseData.message || 'Error al procesar la firma';
            
            return {
                ok: false,
                status: responseData.status,
                message: errorMessage,
                error_details: responseData.errors || [],
                error: true
            };
        }

        // Verificar si la respuesta HTTP no es exitosa
        if (!response.ok) {
            return {
                ok: false,
                status: response.status,
                message: `Error HTTP: ${response.status}`,
                error: true
            };
        }

        // Respuesta exitosa
        return {
            ok: true,
            data: responseData,
            error: false
        };

    } catch (error) {
        console.error('Error en signAttachment:', error);
        return {
            ok: false,
            message: 'Error de conexión al procesar la firma',
            error: true
        };
    }
}

/**
 * 12. GET /api/store-folders/{store-folder-id}/signaturit-attachments
 * Propósito: Obtener información de firmas de Signaturit para documentos de un store folder
 */
function getSignaturitAttachments(storeFolderId) {
    const requestOptions = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(`${global.BASE_API}store-folders/${storeFolderId}/signaturit-attachments`, requestOptions)
        .then(response => handleResponse(response, { ignore401: true }))
        .catch(error => {
            console.error('Error fetching signaturit attachments:', error);
            return { data: [] };
        });
}

/**
 * 13. GET /api/store-folders/attachments/recent?employee_id=<employee_id>
 * Propósito: Obtener los documentos subidos en los últimos 30 días del usuario conectado
 * o del empleado que se pasa por parámetro
 */
function getRecentAttachments(employeeId) {
    let url = `${global.BASE_API}store-folders/attachments/recent`;
    if (employeeId) {
        url += `?employee_id=${encodeURIComponent(employeeId)}`;
    }

    const requestOptions = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(url, requestOptions)
        .then(response => handleResponse(response, { ignore401: true }))
        .catch(error => {
            console.error('Error fetching recent attachments:', error);
            return { data: [] };
        });
}

/**
 * 14. GET /api/store-folders/signaturit/pending?employee_id=<employee_id>
 * Propósito: Obtener los documentos pendientes de firma del usuario conectado
 * o del empleado que se pasa por parámetro
 */
function getPendingSignatures(employeeId) {
    let url = `${global.BASE_API}store-folders/signaturit/pending`;
    if (employeeId) {
        url += `?employee_id=${encodeURIComponent(employeeId)}`;
    }

    const requestOptions = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${authService.token.access_token}`
        },
    };

    return fetch(url, requestOptions)
        .then(response => handleResponse(response, { ignore401: true }))
        .then((parsed) => {
            if (isDocumentFolderDebugEnabled()) {
                const list = Array.isArray(parsed) ? parsed : (parsed?.data || []);
                logAttachmentFolderFields("documentsService.getPendingSignatures (raw parsed)", list, {
                    employeeId,
                    url,
                    parsedKeys: Array.isArray(parsed) ? [] : Object.keys(parsed || {}),
                });
            }
            return parsed;
        })
        .catch(error => {
            console.error('Error fetching pending signatures:', error);
            return { data: [] };
        });
}
